// frontend/admin-portal/src/components/procurement/ReceiptDetailModal.jsx
import React, { useEffect, useState } from "react";
import { Modal, Button, Spinner, Badge, Table } from "@fiberhome/shared-ui";
import { goodsReceiptService } from "../../services/goodsReceiptService";

export default function ReceiptDetailModal({
  open,
  receiptId,
  onClose,
  onCancelled,
}) {
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !receiptId) return;
    setLoading(true);
    setError("");
    goodsReceiptService
      .get(receiptId)
      .then(setReceipt)
      .catch((err) => setError(err.response?.data?.message || err.message))
      .finally(() => setLoading(false));
  }, [open, receiptId]);

  const handleCancel = async () => {
    if (
      !window.confirm("Cancel this receipt? Product stock will be decremented.")
    )
      return;
    setBusy(true);
    try {
      await goodsReceiptService.cancel(receipt.id);
      onCancelled?.();
      onClose?.();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={receipt ? `Receipt ${receipt.code}` : "Receipt"}
      size="lg"
      footer={
        <>
          {receipt && receipt.status === "posted" && (
            <Button variant="danger" onClick={handleCancel} loading={busy}>
              Cancel Receipt
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </>
      }
    >
      {error && <div className="text-danger mb-3">{error}</div>}
      {loading && (
        <div className="fh-settings-loading">
          <Spinner size="lg" />
        </div>
      )}

      {receipt && (
        <>
          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <div className="text-muted" style={{ fontSize: 12 }}>
                Received
              </div>
              <div>{new Date(receipt.receivedAt).toLocaleString()}</div>
            </div>
            <div className="col-md-6">
              <div className="text-muted" style={{ fontSize: 12 }}>
                Supplier
              </div>
              <div>{receipt.supplier?.name || "—"}</div>
            </div>
            <div className="col-md-6">
              <div className="text-muted" style={{ fontSize: 12 }}>
                PO
              </div>
              <div>{receipt.purchaseOrder?.code || "—"}</div>
            </div>
            <div className="col-md-6">
              <div className="text-muted" style={{ fontSize: 12 }}>
                Status
              </div>
              <Badge
                variant={receipt.status === "posted" ? "success" : "default"}
              >
                {receipt.status}
              </Badge>
            </div>
            {receipt.invoiceNumber && (
              <div className="col-md-6">
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Invoice #
                </div>
                <div>{receipt.invoiceNumber}</div>
              </div>
            )}
            {receipt.notes && (
              <div className="col-12">
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Notes
                </div>
                <div style={{ whiteSpace: "pre-wrap" }}>{receipt.notes}</div>
              </div>
            )}
          </div>

          <Table
            columns={[
              {
                key: "product",
                label: "Product",
                render: (r) => (
                  <div>
                    <div style={{ fontWeight: 600 }}>
                      {r.product?.name || r.poItem?.productName}
                    </div>
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      {r.product?.sku}
                    </div>
                  </div>
                ),
              },
              { key: "quantityReceived", label: "Received" },
              {
                key: "unitCost",
                label: "Unit Cost",
                render: (r) => Number(r.unitCost || 0).toLocaleString(),
              },
            ]}
            data={receipt.items || []}
            emptyMessage="No items"
          />
        </>
      )}
    </Modal>
  );
}
